/** Text twin of the Cuts lane: the MV's picture transitions (video.scenes) inside the lanes' view — time, ms, kind,
 * score, shot length — with Seek / In / Out buttons, the whole video's shot statistics and the exports
 * /api/videos/{id}/scenes.{csv,md,json}. Expanded in agent mode. */
import { useMemo } from "react";
import { fmtMs } from "../../lib/frames";
import { apiFileUrl } from "../../lib/identity";
import type { FrameController } from "../player/controller";
import type { InOut } from "../player/Player";
import { SCENE_KIND_LABELS, scenesOf, type View } from "../timeline/project";
import type { SceneStats, Timeline } from "../types";

export const CUTS_TABLE_MAX = 300;

export function sceneStatsText(st: SceneStats | null | undefined): string {
  if (!st) return "";
  const avg = st.avg_shot_ms != null ? `average shot ${(st.avg_shot_ms / 1000).toFixed(2)} s` : "no shots";
  const med = st.median_shot_ms != null ? ` (median ${(st.median_shot_ms / 1000).toFixed(2)} s)` : "";
  const bar = st.per_bar != null ? ` · ${st.per_bar.toFixed(2)} per bar` : "";
  return `${st.cuts} cuts, ${st.dissolves} dissolves / fades, ${st.shots} shots · ${avg}${med} · ${st.per_min.toFixed(1)}`
    + ` per minute${bar}`;
}

export function CutsTable({ tl, ctrl, view, agent, inOut, clock = "MV clock" }: {
  tl: Timeline; ctrl: FrameController; view: View; agent: boolean; inOut?: InOut; clock?: string;
}) {
  const all = scenesOf(tl);
  const rows = useMemo(() => all.filter((s) => s.t_mv_ms >= view.v0 && s.t_mv_ms < view.v1), [all, view]);
  const shown = rows.slice(0, CUTS_TABLE_MAX);
  const step = tl.steps.find((s) => s.entity === "video" && s.step === "scenes");
  const base = `/api/videos/${tl.video_id}/scenes`;
  return (
    <details className="ws8-panel" open={agent} data-testid="cuts-panel">
      <summary><h2 className="ws8-h2 ws8-inline-h">Cuts table</h2></summary>
      <p className="ws8-muted" data-testid="cuts-summary">
        {tl.scene_stats ? `${sceneStatsText(tl.scene_stats)}. ` : `No scene detection yet${
          step ? ` (scenes step: ${step.state.replace("_", " ")}${step.message ? `: ${step.message}` : ""})` : ""}. `}
        Cut = camera cut or angle switch (time = first frame of the new shot); dissolve = cross-dissolve or fade (time =
        middle of the blend); zooms and camera moves are not cuts. Every transition:{" "}
        <a href={apiFileUrl(`${base}.csv`, { download: 1 })} data-testid="cuts-csv">CSV</a>,{" "}
        <a href={apiFileUrl(`${base}.md`)} data-testid="cuts-md">Markdown</a>,{" "}
        <a href={apiFileUrl(`${base}.json`)} data-testid="cuts-json">JSON</a>.
      </p>
      <p className="ws8-muted" data-testid="cuts-count">
        {rows.length > shown.length ? `First ${shown.length} of ${rows.length}` : `${rows.length}`} transitions in{" "}
        {fmtMs(view.v0)}–{fmtMs(view.v1)} (of {all.length})
      </p>
      {rows.length === 0 ? (
        <p data-testid="cuts-empty">{all.length === 0 ? "No picture transitions on this timeline yet." : "No transitions in this view."}</p>
      ) : (
        <div className="ws8-scroll" tabIndex={0} role="region" aria-label="Picture transitions in view">
          <table className="ws8-table ws8-beats-table" data-testid="cuts-table">
            <caption>Picture transitions in the view ({clock})</caption>
            <thead>
              <tr><th scope="col">Time</th><th scope="col">ms</th><th scope="col">Kind</th><th scope="col">Score</th>
                <th scope="col">Shot length</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {shown.map((s) => {
                const kind = SCENE_KIND_LABELS[s.kind] ?? s.kind;
                return (
                  <tr key={s.t_mv_ms} data-testid={`cut-row-${s.t_mv_ms}`} data-kind={s.kind}>
                    <td>{fmtMs(s.t_mv_ms)}</td>
                    <td>{s.t_mv_ms}</td>
                    <td>{kind}{s.dur_ms ? ` (${(s.dur_ms / 1000).toFixed(2)} s)` : ""}</td>
                    <td>{s.score.toFixed(2)}</td>
                    <td>{s.shot_len_ms != null ? `${(s.shot_len_ms / 1000).toFixed(2)} s` : "–"}</td>
                    <td className="ws8-row">
                      <button type="button" data-testid={`cut-seek-${s.t_mv_ms}`} onClick={() => ctrl.seekMs(s.t_mv_ms)}
                        aria-label={`Seek to ${kind} at ${fmtMs(s.t_mv_ms)}`}>Seek</button>
                      {inOut && (
                        <>
                          <button type="button" data-testid={`cut-in-${s.t_mv_ms}`} onClick={() => inOut.set({ in: s.t_mv_ms })}
                            aria-label={`Set In to the ${kind} at ${fmtMs(s.t_mv_ms)}`}>In</button>
                          <button type="button" data-testid={`cut-out-${s.t_mv_ms}`} onClick={() => inOut.set({ out: s.t_mv_ms })}
                            aria-label={`Set Out to the ${kind} at ${fmtMs(s.t_mv_ms)}`}>Out</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
